import { useCallback, useEffect, useRef, useState } from "react";

import { fetchConversationLogs } from "./conversation-logs-api";
import type { AuditAction, ConversationLogEntry } from "./conversation-logs-types";

export interface ConversationLogsFilters {
  startTime: string;
  endTime: string;
  userId: string;
  sessionId: string;
  action: AuditAction | "";
}

export const MAX_RESULTS = 100;

export interface UseConversationLogsReturn {
  entries: ConversationLogEntry[];
  total: number;
  isLoading: boolean;
  error: string | null;
  filters: ConversationLogsFilters;
  setFilters: (filters: ConversationLogsFilters) => void;
  search: () => void;
  selectedEntry: ConversationLogEntry | null;
  selectEntry: (entry: ConversationLogEntry | null) => void;
}

function formatDateTimeLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function defaultTimeRange(): { startTime: string; endTime: string } {
  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  return {
    startTime: formatDateTimeLocal(oneDayAgo),
    endTime: formatDateTimeLocal(now),
  };
}

function defaultFilters(): ConversationLogsFilters {
  const { startTime, endTime } = defaultTimeRange();
  return {
    startTime,
    endTime,
    userId: "",
    sessionId: "",
    action: "",
  };
}

export function useConversationLogs(): UseConversationLogsReturn {
  const [entries, setEntries] = useState<ConversationLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntry, setSelectedEntry] = useState<ConversationLogEntry | null>(null);
  const initialFiltersRef = useRef<ConversationLogsFilters>(defaultFilters());
  const [filters, setFilters] = useState<ConversationLogsFilters>(initialFiltersRef.current);
  const requestIdRef = useRef(0);

  const loadLogs = useCallback(async (currentFilters: ConversationLogsFilters) => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    setSelectedEntry(null);
    try {
      const result = await fetchConversationLogs({
        startTime: new Date(currentFilters.startTime).toISOString(),
        endTime: new Date(currentFilters.endTime).toISOString(),
        ...(currentFilters.userId ? { userId: currentFilters.userId } : {}),
        ...(currentFilters.sessionId ? { sessionId: currentFilters.sessionId } : {}),
        ...(currentFilters.action ? { action: currentFilters.action } : {}),
        limit: MAX_RESULTS,
      });
      if (requestIdRef.current !== requestId) return;
      setEntries(result.entries);
      setTotal(result.total);
    } catch (e) {
      if (requestIdRef.current !== requestId) return;
      setError(e instanceof Error ? e.message : "Failed to load conversation logs");
      setEntries([]);
      setTotal(0);
    } finally {
      if (requestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, []);

  const search = useCallback(() => {
    void loadLogs(filters);
  }, [filters, loadLogs]);

  useEffect(() => {
    void loadLogs(initialFiltersRef.current);
  }, [loadLogs]);

  return {
    entries,
    total,
    isLoading,
    error,
    filters,
    setFilters,
    search,
    selectedEntry,
    selectEntry: setSelectedEntry,
  };
}
